import { useTranslation } from 'react-i18next'
import type { CamionMuelle, CargaSalida, CuadroMuelles, EstadoCamion } from '@/types'
import type { DockTrack } from '@/utils/dockTimeTracks'
import { COLOR_ESTADO, estadoCamionDe, hhmm, paletsDeCamion, tramoDe } from '@/utils/camionesMuelle'
import { horasOcupadas } from '@/utils/dockOcupacion'

/**
 * Los KPIs del cuadro, en píldoras finas y no en `KpiCard`.
 *
 * La pantalla necesita el alto para el gantt: seis tarjetas de ochenta píxeles
 * dejarían el cuadro en la mitad inferior, y el cuadro es la pantalla.
 */
export function DockKpiStrip({
  cuadro, track, cargasPorNumero, sinMuelle, dark,
}: {
  cuadro: CuadroMuelles
  track: DockTrack
  cargasPorNumero: Map<string, CargaSalida>
  sinMuelle: number
  dark: boolean
}) {
  const { t } = useTranslation()

  const cargasDe = (c: CamionMuelle) =>
    c.cargas.map((n) => cargasPorNumero.get(n)).filter((x): x is CargaSalida => Boolean(x))

  const estados = cuadro.camiones.map((c) => estadoCamionDe(c, cargasDe(c)))
  const cuenta = (e: EstadoCamion) => estados.filter((x) => x === e).length

  // Los palets salen de la SUMA de las cargas de cada camión (estimada del
  // maestro de unidades por palet), y el campo tecleado gana cuando existe.
  let palets: number | null = null
  let paletsSinDato = 0
  for (const c of cuadro.camiones) {
    const p = paletsDeCamion(c, cargasDe(c))
    if (p.total === null) paletsSinDato += 1
    else palets = (palets ?? 0) + p.total
  }

  const intervalos = cuadro.camiones
    .map(tramoDe)
    .filter((x): x is { iniMs: number; finMs: number } => x !== null)
  const horas = horasOcupadas(intervalos, track.rangeStart.getTime(), track.rangeEnd.getTime())
  const disponibles = track.horasOperativas * Math.max(1, cuadro.muelles.length)

  const ahora = Date.now()
  const proxima = cuadro.camiones
    .map((c) => ({ c, tr: tramoDe(c) }))
    .filter((x) => x.tr && x.tr.iniMs > ahora)
    .sort((a, b) => a.tr!.iniMs - b.tr!.iniMs)[0]

  const num = (n: number) => n.toLocaleString('es-ES')

  const pildoras: { clave: string; valor: string; sub?: string; tono?: string }[] = [
    { clave: 'salidas', valor: num(cuadro.camiones.length) },
    { clave: 'salidos', valor: num(cuenta('salido')) },
    { clave: 'cargando', valor: num(cuenta('cargando')), tono: COLOR_ESTADO.cargando.texto },
    {
      clave: 'palets',
      // Si NINGÚN camión lo tiene informado, el total es hueco y no cero: el
      // palets lo teclea una persona, y un cero diría que no hay nada que mover.
      valor: palets === null ? '—' : num(palets),
      sub: paletsSinDato > 0 ? t('muelles.kpi.paletsSinDato', { count: paletsSinDato }) : undefined,
    },
    {
      clave: 'ocupacion',
      valor: `${Math.round(disponibles > 0 ? (horas / disponibles) * 100 : 0)}%`,
      sub: t('muelles.kpi.ocupacionSub', {
        horas: horas.toLocaleString('es-ES', { maximumFractionDigits: 1 }),
        total: num(Math.round(disponibles)),
      }),
    },
    {
      clave: 'sinMuelle',
      valor: num(sinMuelle),
      tono: sinMuelle > 0 ? 'text-rose-500' : undefined,
    },
    {
      clave: 'proxima',
      valor: proxima ? hhmm(proxima.c.fechaInicio) : '—',
      sub: proxima?.c.muelle ?? undefined,
    },
  ]

  const bdr = dark ? 'border-white/10' : 'border-slate-200'
  const txtMuted = dark ? 'text-slate-500' : 'text-slate-400'

  return (
    <div className="flex flex-wrap gap-2">
      {pildoras.map((p) => (
        <div
          key={p.clave}
          className={`px-3 py-1.5 rounded-lg border ${bdr} ${dark ? 'bg-slate-900/40' : 'bg-white'}`}
        >
          <div className={`text-[9px] font-bold uppercase tracking-wider ${txtMuted}`}>
            {t(`muelles.kpi.${p.clave}`)}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className={`text-[15px] font-black tabular-nums ${p.tono ?? (dark ? 'text-slate-100' : 'text-slate-900')}`}>
              {p.valor}
            </span>
            {p.sub && <span className={`text-[10px] ${txtMuted}`}>{p.sub}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}
